import { useMemo, useRef, useEffect } from 'react';
import Image from 'next/image';
import Container from '@components/global/Container';
import Heading from '@components/ui/Heading';
import Button from '@components/ui/Button';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import { CollectionAssortmentSlice } from '@adapters/prismic/types/brandedStorePage';
import ProductCardAlt from '@components/global/Recommendations/ProductCardAlt';
import { AlgonomyProduct } from '@components/global/Recommendations/Loader';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
  CollectionAssortmentEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import useObjectVisibility from '@hooks/useObjectVisibility';

import cn from 'classnames';
import styles from './CollectionAssortment.module.scss';

export const CollectionAssortment = ({
  locale,
  primary,
  items,
  products,
  brandedStoreCoopAttribute,
  sliceIndex,
}: CollectionAssortmentSlice) => {
  const {
    collection_logo,
    collection_image,
    collection_title,
    copy,
    collection_cta_label,
    collection_cta_importance,
    collection_cta_link,
    quantity_of_products,
    content_left,
  } = primary;

  const display4ProductsButton =
    quantity_of_products === '4' &&
    collection_cta_label &&
    collection_cta_importance &&
    collection_cta_link;

  const display8ProductsButton =
    quantity_of_products === '8' &&
    collection_cta_label &&
    collection_cta_importance &&
    collection_cta_link;

  const buttonPromotionTracking = useMemo(
    () => ({
      page: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.CollectionAssortment,
      },
      ecommerce: {
        creative_name: `link,${collection_cta_link}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.CollectionAssortment,
          itemIndex: display4ProductsButton ? 0 : products?.length ?? 0,
        }),
        promotion_id: collection_title || null,
        promotion_name: collection_cta_label || null,
        isVideo: false,
      },
    }),
    [
      brandedStoreCoopAttribute,
      collection_cta_label,
      collection_cta_link,
      collection_title,
      display4ProductsButton,
      products?.length,
      sliceIndex,
    ]
  );

  const buttonRef = useRef<HTMLButtonElement>(null);
  const isButtonVisible = useObjectVisibility(buttonRef);

  useEffect(() => {
    if (isButtonVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...buttonPromotionTracking,
      } as CollectionAssortmentEventProps);
    }
  }, [isButtonVisible, buttonPromotionTracking, products]);

  return (
    <div id={primary?.id}>
      <Container>
        <div
          className={cn(styles.gridContainer, {
            [styles.contentLeft]: content_left,
          })}
        >
          <div className={styles.leftColumn}>
            {collection_logo?.url && (
              <div className={styles.logoContainer}>
                <Image
                  src={collection_logo?.url}
                  alt={collection_logo?.alt || ''}
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
            {collection_image?.url && (
              <div
                className={styles.imageContainer}
                style={{
                  aspectRatio: `${collection_image?.dimensions?.width}/${collection_image?.dimensions?.height}`,
                }}
              >
                <Image
                  src={collection_image?.url}
                  alt={collection_image?.alt || ''}
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
            {collection_title && (
              <Heading
                semanticLevel="h2"
                styleLevel="1"
                className={styles.heading}
              >
                {collection_title}
              </Heading>
            )}
            {copy?.[0]?.text && (
              <MarkdownCopy
                text={copy?.[0]?.text ?? ''}
                className={styles.copy}
              />
            )}
            {items && items.length > 0 && (
              <div className={styles.iconsContainer}>
                {items.map((item: any, i: number) => (
                  <div
                    key={`collection-icon-${i}`}
                    className={styles.iconContainer}
                  >
                    <div className={styles.icon}>
                      <Image
                        src={item?.icon?.url}
                        alt={item?.icon?.alt}
                        fill
                        sizes="100vw"
                        style={{
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                    <div>
                      <p className={styles.iconDescription}>
                        {item?.icon_description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {display4ProductsButton && (
              <Button
                className={styles.cta}
                label={collection_cta_label}
                size="large"
                variant={collection_cta_importance}
                type="button"
                href={`/${locale}/${collection_cta_link}`}
                ref={buttonRef}
                handleClick={() => {
                  trackPromotionEvent({
                    eventName: PromotionEventName.selectPromotion,
                    ...buttonPromotionTracking,
                  } as CollectionAssortmentEventProps);
                }}
              />
            )}
          </div>
          <div className={styles.recommendationsContainer}>
            {products?.map((product: AlgonomyProduct, index) => {
              const promotionTracking = {
                page: {
                  path: window.location.pathname,
                  page_type: determinePageType(),
                },
                interaction: {
                  coop_brand: brandedStoreCoopAttribute || null,
                  interaction_location:
                    InteractionLocation.CollectionAssortment,
                },
                ecommerce: {
                  creative_name: `link,${product.detailURL}`,
                  creative_slot: buildCreativeSlotName({
                    sliceIndex,
                    interactionLocation:
                      InteractionLocation.CollectionAssortment,
                    itemIndex: index + (display4ProductsButton ? 1 : 0),
                  }),
                  promotion_id: product?.displayName || null,
                  promotion_name: product?.displayName || null,
                  isVideo: false,
                },
              };
              return (
                <ProductCardAlt
                  key={`${product.productUrl}-${product.displayName}`}
                  displayName={product.displayName}
                  displayPrice={product.displayPrice}
                  productImage={product.imageURL}
                  productUrl={product?.linkURL ?? product?.detailURL}
                  salePrice={product.displaySalePrice}
                  trackClick={() => {
                    trackPromotionEvent({
                      eventName: PromotionEventName.selectPromotion,
                      ...promotionTracking,
                    } as CollectionAssortmentEventProps);
                  }}
                  trackView={() => {
                    trackPromotionEvent({
                      eventName: PromotionEventName.viewPromotion,
                      ...promotionTracking,
                    } as CollectionAssortmentEventProps);
                  }}
                />
              );
            })}
          </div>
        </div>
      </Container>
      {display8ProductsButton && (
        <Container className={styles.buttonContainer}>
          <Button
            label={collection_cta_label}
            size="large"
            variant={collection_cta_importance}
            type="button"
            href={`/${locale}/${collection_cta_link}`}
            ref={buttonRef}
            handleClick={() => {
              trackPromotionEvent({
                eventName: PromotionEventName.selectPromotion,
                ...buttonPromotionTracking,
              } as CollectionAssortmentEventProps);
            }}
          />
        </Container>
      )}
    </div>
  );
};

export default CollectionAssortment;
